import { useToast } from "contexts/ToastContext";
import { useHasRole } from "contexts/AuthContext";
import { useState } from "react";
import { editAppealCouncilLitigation } from "services/prosecutorDecipilineService";
import { CouncilLitigation } from "types";
import { ROLES } from "utils/roles";
import AddCouncilMeetingAttendance from "./AddCouncilMeetingAttendance";
import { useTranslation } from "contexts/i18nContext";
import DocumentViewerModal from "components/DocumentViewer";

interface CouncilLitigationProps {
  councilLitigationData: CouncilLitigation[];
  onCouncilLitigationRefresh: any;
}

const calculate_remaining_days = ({ date }: { date: string }) => {
  const appeal = new Date(date ?? "");
  const today = new Date();

  // Zero out time to compare dates only
  appeal.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const msPerDay = 1000 * 60 * 60 * 24;
  const diffInMs = appeal.getTime() - today.getTime();

  const remainingDays = Math.floor(diffInMs / msPerDay);

  return remainingDays;
};

export default function AppealCouncilLitigationCard({
  councilLitigationData,
  onCouncilLitigationRefresh,
}: CouncilLitigationProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();

  const canViewDailyHearing = useHasRole([
    ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
    ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
    ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);
  const canAddMeetingAttendance = useHasRole([
    ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
    ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);

  const [isOpen, setIsOpen] = useState(false);
  const [selectedLitigation, setSelectedLitigation] = useState<any>(null);
  const [editingDateId, setEditingDateId] = useState<string | null>(null);
  const [dateDraft, setDateDraft] = useState({
    hearing_date: "",
    next_appointment_date: "",
  });
  const [isDateUpdating, setIsDateUpdating] = useState(false);

  const toDateInputValue = (value?: string) => {
    if (!value) return "";
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toISOString().slice(0, 10);
  };

  const startDateEdit = (item: CouncilLitigation) => {
    setEditingDateId(item.id ?? null);
    setDateDraft({
      hearing_date: toDateInputValue(item.hearing_date as string),
      next_appointment_date: toDateInputValue(
        item.next_appointment_date as string,
      ),
    });
  };

  const cancelDateEdit = () => {
    setEditingDateId(null);
    setDateDraft({ hearing_date: "", next_appointment_date: "" });
  };

  const saveDateEdit = async (item: CouncilLitigation) => {
    if (!item?.id) {
      showToast("Litigation id not found", "error");
      return;
    }

    const currentHearingDate = toDateInputValue(item.hearing_date as string);
    const currentNextDate = toDateInputValue(
      item.next_appointment_date as string,
    );

    const payload: Record<string, any> = {};

    if (
      dateDraft.hearing_date &&
      dateDraft.hearing_date !== currentHearingDate
    ) {
      payload.hearing_date = dateDraft.hearing_date;
    }

    if (
      dateDraft.next_appointment_date &&
      dateDraft.next_appointment_date !== currentNextDate
    ) {
      payload.next_appointment_date = dateDraft.next_appointment_date;
    }

    if (!payload.hearing_date && !payload.next_appointment_date) {
      showToast("No date change detected", "error");
      return;
    }

    try {
      setIsDateUpdating(true);
      await editAppealCouncilLitigation(payload as any, item.id, false);
      await Promise.resolve(onCouncilLitigationRefresh?.());
      cancelDateEdit();
      showToast("Date updated", "success");
    } catch (err) {
      showToast("Failed to update date", "error");
    } finally {
      setIsDateUpdating(false);
    }
  };

  return (
    <div className="mb-8">
      {councilLitigationData[0]?.council_decision_day && (
        <div
          className={
            "mx-auto bg-white border-black border-8 rounded-xl  overflow-hidden mb-4"
          }
        >
          <div className="p-5">
            <h1 className="text-1xl font-bold text-gray-800 mb-3">
              {t("officeOfCouncilDecision")}
            </h1>

            <div className="w-full flex flex-wrap items-center justify-between gap-4 my-3">
              <p className="block  text-gray-600">{t("submissionDate")}</p>
              <p className="block  text-black font-medium">
                {councilLitigationData[0]?.council_decision_day || "-"}
              </p>
            </div>

            <hr />

            {councilLitigationData[0]?.council_decision_type && (
              <div className="my-3 mb-5">
                <p className="text-[#002855] font-medium">
                  {t("councilDecisionType")}
                </p>
                <p className="text-gray-700 mt-1">
                  {councilLitigationData[0]?.council_decision_type || "-"}
                </p>
              </div>
            )}

            {councilLitigationData[0]?.discipline_measure_type && (
              <div className="my-3 mb-5">
                <p className="text-[#002855] font-medium">
                  {t("decisionMeasureType")}
                </p>
                <p className="text-gray-700 mt-1">
                  {councilLitigationData[0]?.discipline_measure_type || "-"}
                </p>
              </div>
            )}

            {/* Summary Desc */}
            <div className="my-3 mb-5">
              <p className="text-[#002855] font-medium">
                {t("councilDecisionSummary")}
              </p>
              <p className="text-gray-700 mt-1">
                {councilLitigationData[0]?.council_decision_summary || "-"}
              </p>
            </div>

            {/* Files */}
            {councilLitigationData[0]?.documents && (
              <DocumentViewerModal
                documents={(councilLitigationData[0]?.documents || []) as any}
                baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/litigation-counciloffice-documents/document`}
              />
            )}
          </div>
        </div>
      )}

      <div className="space-y-4">
        {[...(councilLitigationData || [])]
          ?.sort((a, b) => {
            const aHasDecision = !!a.council_decision_summary;
            const bHasDecision = !!b.council_decision_summary;

            // If only one has a decision, show it first
            if (aHasDecision && !bHasDecision) return -1;
            if (!aHasDecision && bHasDecision) return 1;

            // Otherwise, sort by next appointment date descending
            const dateA = new Date(a?.next_appointment_date ?? "").getTime();
            const dateB = new Date(b?.next_appointment_date ?? "").getTime();

            return dateA - dateB;
          })

          ?.filter((obj, index) => !obj.council_decision_day)
          ?.map((obj) =>
            canViewDailyHearing || obj?.council_decision_summary ? (
              <div
                key={obj.id}
                className={
                  obj?.council_decision_summary
                    ? "shadow-md rounded-xl p-4 w-full mx-auto bg-white border-l-8 border-black"
                    : "shadow-md rounded-xl p-4 w-full mx-auto"
                }
              >
                {/* Header */}
                {/* <div className="flex items-center gap-3 mb-4">
            <img
              src={require("../../../images/avatar.png")}
              alt="profile"
              className="w-10 h-10 rounded-full object-cover"
            />
            <div>
              <h2 className="text-sm font-semibold">Senay Daniel</h2>
              <p className="text-xs text-gray-500">Investigation Team</p>
            </div>
          </div> */}

                {obj?.hearing_date && (
                  <>
                    <div className="w-full flex flex-wrap items-center justify-between gap-4 my-2">
                      <p className="block text-gray-600">{t("hearingDate")}</p>
                      <div className="flex items-center gap-3">
                        <p className="block text-black font-medium">
                          {obj?.hearing_date || "-"}
                        </p>
                        <button
                          type="button"
                          onClick={() => startDateEdit(obj)}
                          className="text-xs font-medium text-red-700 hover:text-red-800 underline"
                        >
                          {t("updateDate")}
                        </button>
                      </div>
                    </div>

                    <hr />
                  </>
                )}

                {obj?.next_appointment_date && (
                  <>
                    <div className="w-full flex flex-wrap items-center justify-between gap-4 my-2">
                      <p className="block text-gray-600">
                        {t("nextAppointmentDate")}
                      </p>
                      <p className="block text-black font-medium">
                        {obj?.next_appointment_date || "-"}
                      </p>
                    </div>

                    <hr />
                  </>
                )}

                {editingDateId === obj.id && (
                  <div className="my-3 rounded-md border border-gray-200 bg-gray-50 p-3 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {t("hearingDate")}
                        </label>
                        <input
                          type="date"
                          value={dateDraft.hearing_date}
                          onChange={(e) =>
                            setDateDraft((prev) => ({
                              ...prev,
                              hearing_date: e.target.value,
                            }))
                          }
                          className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {t("nextAppointmentDate")}
                        </label>
                        <input
                          type="date"
                          value={dateDraft.next_appointment_date}
                          onChange={(e) =>
                            setDateDraft((prev) => ({
                              ...prev,
                              next_appointment_date: e.target.value,
                            }))
                          }
                          className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={isDateUpdating}
                        onClick={() => saveDateEdit(obj)}
                        className="rounded bg-red-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-800 disabled:opacity-60"
                      >
                        {isDateUpdating ? "Updating..." : "Save Date"}
                      </button>
                      <button
                        type="button"
                        disabled={isDateUpdating}
                        onClick={cancelDateEdit}
                        className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {obj?.given_order && (
                  <>
                    {" "}
                    <div className="w-full flex flex-wrap items-center justify-between gap-4 my-2">
                      <p className="block text-gray-600">{t("councilOrder")}</p>
                      <p className="block text-black font-medium">
                        {obj?.given_order || "-"}
                      </p>
                    </div>
                    <hr />
                  </>
                )}

                {obj?.council_decision_day && (
                  <>
                    <div className="w-full flex flex-wrap items-center justify-between gap-4 my-2">
                      <p className="block text-gray-600">
                        {t("remainingDaysForAppeal")}
                      </p>
                      {(() => {
                        const days = calculate_remaining_days({
                          date: obj?.council_decision_day ?? "",
                        });
                        const isExpired = days < 0;
                        const absDays = Math.abs(days);
                        const dayLabel = absDays === 1 ? t("day") : t("days");

                        if (!obj?.council_decision_day) return <>-</>;

                        return (
                          <p
                            className={`block font-medium ${
                              isExpired ? "text-red-600" : "text-green-700"
                            }`}
                          >
                            {isExpired
                              ? `${t("expired")} ${absDays} ${dayLabel} ${t("ago")}`
                              : `${days} ${dayLabel}`}
                          </p>
                        );
                      })()}
                    </div>

                    <hr />
                  </>
                )}

                {/* Info Rows */}
                {obj?.daily_report_summary && (
                  <div className="my-3 mb-5">
                    <p className="text-[#002855] font-medium">
                      {t("dailyReportSummary")}
                    </p>
                    <p className="text-gray-700 mt-1">
                      {obj?.daily_report_summary || "-"}
                    </p>
                  </div>
                )}

                {obj?.appointment_reason && (
                  <div className="my-3 mb-5">
                    <p className="text-[#002855] font-medium">
                      {t("appointmentReason")}
                    </p>
                    <p className="text-gray-700 mt-1">
                      {obj?.appointment_reason || "-"}
                    </p>
                  </div>
                )}
                {obj?.documents && obj?.documents.length > 0 && (
                  <div className="mt-2">
                    <DocumentViewerModal
                      documents={obj?.documents as any}
                      baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/litigation-counciloffice-documents/document`}
                    />
                  </div>
                )}

                {obj?.attendances &&
                  Array.isArray(obj.attendances) &&
                  obj.attendances.length > 0 && (
                    <div className="my-3 mb-5">
                      <p className="text-[#002855] font-medium mb-2">
                        {t("attendances")}
                      </p>
                      <div className="space-y-2">
                        {obj.attendances.map((attendance: any) => (
                          <div
                            key={attendance.id}
                            className="border rounded p-2 bg-gray-50 flex flex-col gap-2"
                          >
                            <div>
                              <span className="font-semibold text-gray-700">
                                {t("member")}:
                              </span>
                              <span>{attendance.council_member}</span>
                            </div>
                            <div className="flex justify-between">
                              <div>
                                <span className="font-semibold text-gray-700">
                                  {t("present")}:
                                </span>
                                <span
                                  className={
                                    attendance.was_present
                                      ? "text-green-700"
                                      : "text-red-600"
                                  }
                                >
                                  {attendance.was_present ? t("yes") : t("no")}
                                </span>
                              </div>

                              <div>
                                <span className="font-semibold text-gray-700">
                                  {t("date")}:
                                </span>
                                <span>
                                  {new Date(
                                    attendance.createdAt,
                                  ).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                            {attendance.remark && (
                              <div>
                                <span className="font-semibold text-gray-700">
                                  {t("remark")}:
                                </span>
                                <span>{attendance.remark}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {obj?.council_decision_summary && (
                  <div className="my-3 mb-5">
                    <p className="text-[#002855] font-medium">
                      {t("councilDecisionSummary")}
                    </p>
                    <p className="text-gray-700 mt-1">
                      {obj?.council_decision_summary || "-"}
                    </p>
                  </div>
                )}

                {/* Files */}

                {canAddMeetingAttendance &&
                  !councilLitigationData[0]?.council_decision_day && (
                    <button
                      className="w-full bg-[#5f5f5f] hover:bg-gray-800 border border-white text-white py-2 rounded-lg mt-2"
                      onClick={() => {
                        setSelectedLitigation(obj);
                        setIsOpen(true);
                      }}
                    >
                      {t("addAttendance")}
                    </button>
                  )}
              </div>
            ) : (
              <></>
            ),
          )}
      </div>

      {councilLitigationData && (
        <AddCouncilMeetingAttendance
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          litigationData={selectedLitigation}
          onRefresh={onCouncilLitigationRefresh}
        />
      )}
    </div>
  );
}
